import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton
} from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from '@mui/icons-material/Search';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { fetchReport, handleError, formatDateForMySQL, formatCurrency } from '../../utils/lib';
import { useSnackbar } from '../../providers/SnackBarProvider';
import useNavigationHelperStore from '../../store/navigationHelperStore';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { useReportDatePickerLocale } from '../../hooks/useReportDatePickerLocale';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff6b6b', '#4ecdc4', '#45b7d1'];

const SellsByCategoryReport = () => {
  const { t } = useTranslation();
  const { datePickerFormat, dayjsPickerLocale } = useReportDatePickerLocale();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const snackbar = useSnackbar();
  const { setNavigation, state } = useNavigationHelperStore();
  
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());

  useEffect(() => {
    if (state) {
      const { startDate: savedStartDate, endDate: savedEndDate } = state;
      if (savedStartDate) setStartDate(dayjs(savedStartDate));
      if (savedEndDate) setEndDate(dayjs(savedEndDate));
      handleSearch();
    }
  }, []);

  const saveCurrentState = () => {
    setNavigation('/main/reports', {
      startDate: startDate.toString(),
      endDate: endDate.toString(),
      data
    });
  };

  const handleSearch = async () => {
    setIsLoading(true);
    try {
      const formattedStartDate = formatDateForMySQL(startDate);
      const formattedEndDate = formatDateForMySQL(endDate);
      
      const params = {
        from: formattedStartDate,
        to: formattedEndDate
      };
      
      const response = await fetchReport('SELLS_BY_CATEGORY', 1, params);
      setData(response);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const getPieChartData = () => {
    if (!data || !data.content || data.content.length === 0) {
      return [];
    }

    return data.content.map(item => ({
      name: item.category_name,
      value: Number(item.subtotal) || 0,
      count: item.sells_count || 0
    }));
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={dayjsPickerLocale}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5}>
              <DatePicker
                label={t('common.from')}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format={datePickerFormat}
                slotProps={{
                  textField: { fullWidth: true, variant: 'outlined' },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={5}>
              <DatePicker
                label={t('common.to')}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format={datePickerFormat}
                minDate={startDate}
                slotProps={{
                  textField: { fullWidth: true, variant: 'outlined' },
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={2}>
              <IconButton 
                onClick={handleSearch}
                disabled={isLoading}
                sx={{ 
                  p: 1, 
                  backgroundColor: 'primary.main', 
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: 'action.disabledBackground',
                  }
                }}
              >
                <SearchIcon />
              </IconButton>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>
      
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : data ? (
        <>
          {data.content && data.content.length > 0 ? (
            <>
              <Box mb={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6">
                      {t('reports.sellsByCategoryTitle')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Paper sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                      <Typography variant="h5">
                        {t('reports.sellsTotal')} {formatCurrency(data.total || 0)}
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>

              {data.content.length > 1 && (
                <Box mb={3}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" mb={2}>{t('reports.salesDistribution')}</Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={getPieChartData()}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {getPieChartData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name, props) => [
                            formatCurrency(value),
                            `${t('reports.salesLegend')} ${props.payload.count}`
                          ]}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Box>
              )}
              
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table sx={{ minWidth: 350 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('common.category')}</TableCell>
                      <TableCell align="center">{t('reports.salesCount')}</TableCell>
                      <TableCell align="right">{t('reports.subtotal')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.content.map((category) => (
                      <TableRow 
                        key={category.category_id}
                        hover
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {category.category_name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {category.sells_count}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(category.subtotal)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell>
                        <Typography variant="body1" fontWeight="bold">
                          {t('reports.totalLabel')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body1" fontWeight="bold">
                          {data.content.reduce((sum, cat) => sum + cat.sells_count, 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1" fontWeight="bold">
                          {formatCurrency(data.total)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Box textAlign="center" p={3}>
              <Typography variant="body1">{t('reports.noSellsByCategory')}</Typography>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" p={3}>
          <Typography variant="body1">{t('reports.selectDateAndCategory')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default SellsByCategoryReport;
